import React from "react";
import { Text } from "@chakra-ui/react";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Report } from "../../../../types/reports";
import type { ReportTypeLaneConfig } from "../reportsTypeLanes";
import { buildGenerateReportPathForLane } from "../reportsTypeLanes";
import { ReportListCard } from "./ReportListCard";

export interface ReportsTypeLaneProps {
  lane: ReportTypeLaneConfig;
  reports: Report[];
  selectedIds: Set<string>;
  onSelectedChange: (reportId: string, selected: boolean) => void;
  onOpenReport: (report: Report) => void;
  onDeleteReport: (report: Report) => void;
  deletingReportId: string | null;
  isDeletingReports: boolean;
  onGenerateClick: (path: string) => void;
  className?: string;
}

export function ReportsTypeLane({
  lane,
  reports,
  selectedIds,
  onSelectedChange,
  onOpenReport,
  onDeleteReport,
  deletingReportId,
  isDeletingReports,
  onGenerateClick,
  className,
}: ReportsTypeLaneProps): React.ReactElement {
  const generatePath = buildGenerateReportPathForLane(lane.key);

  return (
    <section
      data-reports-type-lane=""
      data-reports-type-lane-key={lane.key}
      className={cn(
        "flex min-h-0 min-w-0 flex-1 flex-col rounded-[6px] border border-border bg-card/50 dark:bg-sidebar/40",
        lane.laneAccentClass,
        className,
      )}
    >
      <header
        data-reports-type-lane-header=""
        className="flex shrink-0 flex-col gap-2 border-b border-border px-3 py-3"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-[family-name:var(--font-primary)] text-sm font-semibold text-foreground">
                {lane.label}
              </h3>
              <Badge
                variant="outline"
                className="border-border font-[family-name:var(--font-primary)] font-normal text-muted-foreground"
              >
                {reports.length}
              </Badge>
            </div>
            <p
              data-reports-muted-text=""
              className="mt-1 font-[family-name:var(--font-primary)] text-xs leading-snug text-gray-500 dark:text-sidebar-foreground"
            >
              {lane.subtitle}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            className="h-7 shrink-0 gap-1 px-2 normal-case tracking-normal"
            onClick={() => onGenerateClick(generatePath)}
          >
            <Plus className="size-3.5" aria-hidden />
            <span className="sr-only sm:not-sr-only">Generate</span>
          </Button>
        </div>
      </header>

      <div
        data-reports-type-lane-body=""
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-3"
        style={{ maxHeight: "min(70vh, 720px)" }}
      >
        {reports.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-[6px] border border-dashed border-border px-4 py-8 text-center">
            <Text
              fontSize="sm"
              color="gray.500"
              fontFamily="var(--font-primary)"
              lineHeight="1.5"
            >
              No {lane.label.toLowerCase()} reports on this page yet.
            </Text>
            <Button
              type="button"
              variant="outline-brand"
              size="sm"
              data-font="primary"
              className="normal-case tracking-normal"
              onClick={() => onGenerateClick(generatePath)}
            >
              Generate {lane.label}
            </Button>
          </div>
        ) : (
          reports.map((report) => (
            <ReportListCard
              key={report.id}
              report={report}
              selected={selectedIds.has(report.id)}
              onSelectedChange={(next) => onSelectedChange(report.id, next)}
              onOpen={onOpenReport}
              onDelete={onDeleteReport}
              deleting={deletingReportId === report.id}
              deleteDisabled={
                isDeletingReports &&
                deletingReportId != null &&
                deletingReportId !== report.id
              }
            />
          ))
        )}
      </div>
    </section>
  );
}
